import React, { useMemo } from "react";
import { Box } from "@material-ui/core";
import { BillFileInfo, Transaction } from "../../../api/services";
import { TransactionSummary } from "./TransactionSummary";
import { formatSpacedStringSentenceCase } from "../../../utils/formatStringSpacing";
import { FileAction } from "./FileAction";
import { formatDate } from "../../../utils/formatDate";
import {
  CustomTableRow,
  MultiLineTableCell,
  SingleLineTableCell,
} from "../../../components/CustomTableComponents";
import { TransactionDetailsLink } from "./DetailsLink";
import {
  formatMoneyWithCR,
  formatMoneyWithSign,
} from "../../../utils/formatMoney";

export interface TransactionRowProps {
  transaction: Transaction;
}

const transactionTypeDisplayMap: { [index: string]: string } = {
  CreditNote: "Credit Note",
  "Write Off": "Write-off",
  "Credit Write Off": "Credit write-off",
  "PAYG Transfer": "PAYG transfer",
};
export const formatTransactionType = (transType: string) => {
  return transType in transactionTypeDisplayMap
    ? transactionTypeDisplayMap[transType]
    : formatSpacedStringSentenceCase(transType);
};

export const TransactionRow: React.FC<TransactionRowProps> = ({
  transaction,
}) => {
  const { debitAmount, creditAmount } = useMemo(() => {
    const amount = transaction.amount;
    if (Number(amount) >= 0) {
      return { creditAmount: null, debitAmount: amount };
    } else {
      return {
        debitAmount: null,
        creditAmount: amount,
      };
    }
  }, [transaction.amount]);

  const { files } = transaction as any;
  const pdfFile = useMemo(() => {
    const pdfFiles = ((files || []) as BillFileInfo[])
      .filter((a) => a.fileType === "pdf")
      .sort((a, b) => a.orderNo.localeCompare(b.orderNo));
    return pdfFiles.find((a) => a.status === "Ready") || pdfFiles[0];
  }, [files]);

  const accountBalance = useMemo(() => {
    const balance = transaction.accountBalance;
    if (balance) {
      return formatMoneyWithCR(transaction.currency, balance, { showDR: true });
    } else {
      return null;
    }
  }, [transaction.currency, transaction.accountBalance]);

  const isStrikethrough =
    transaction.isCancelled ||
    transaction.status === "Rejected" ||
    transaction.isCancellation;
  const isGreyedOut =
    transaction.status !== "Accepted" ||
    transaction.isCancelled ||
    transaction.isCancellation;

  return (
    <CustomTableRow
      isStruck={isStrikethrough}
      isGrey={isGreyedOut}
      height="50px"
    >
      <SingleLineTableCell data-testid="TransactionRow__TableCell--Date">
        {transaction.resolvedDttm
          ? formatDate(transaction.resolvedDttm)
          : transaction.status}
      </SingleLineTableCell>
      <SingleLineTableCell data-testid="TransactionRow__TableCell--Type">
        {formatTransactionType(transaction.type)}
      </SingleLineTableCell>
      <MultiLineTableCell padding="none">
        <TransactionSummary transaction={transaction} />
      </MultiLineTableCell>
      <SingleLineTableCell data-testid="TransactionRow__TableCell--Debit">
        {debitAmount && (
          <Box textAlign="right">
            {`${formatMoneyWithSign(transaction.currency, debitAmount)}`}
          </Box>
        )}
      </SingleLineTableCell>
      <SingleLineTableCell data-testid="TransactionRow__TableCell--Credit">
        {creditAmount && (
          <Box textAlign="right">
            {`${formatMoneyWithSign(transaction.currency, creditAmount)}`}
          </Box>
        )}
      </SingleLineTableCell>
      <SingleLineTableCell data-testid="TransactionRow__TableCell--Balance">
        {accountBalance != null && (
          <Box textAlign="right">{accountBalance}</Box>
        )}
      </SingleLineTableCell>
      <SingleLineTableCell data-testid="TransactionRow__TableCell--Actions">
        {/* set inline-block so it wont be struck through */}
        <Box display="inline-block" width="100%">
          <Box display="flex" alignItems="center">
            <FileAction file={pdfFile} />
            <TransactionDetailsLink transaction={transaction} />
          </Box>
        </Box>
      </SingleLineTableCell>
    </CustomTableRow>
  );
};
